import { NavLink, useNavigate } from 'react-router-dom';
import { motion, LayoutGroup } from 'framer-motion';
import { useId } from 'react';
import { LayoutDashboard, MessageSquare, BookOpen, TrendingUp, Trophy, User, LogOut, Zap, X, Flame, ChevronRight, Crown, FileText, CalendarDays, ShieldCheck, Sparkles, MessageSquarePlus } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription } from '../contexts/SubscriptionContext';

const ADMIN_EMAILS = ['taniknpoojari@gmail.com', 'truecursemahito28@gmail.com', 'samanvayrakesh7@gmail.com'];

const NAV = [
  { to: '/', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { to: '/chat', icon: MessageSquare, label: 'AI Tutor' },
  { to: '/syllabus', icon: BookOpen, label: 'Syllabus' },
  { to: '/progress', icon: TrendingUp, label: 'Progress' },
  { to: '/quiz', icon: Trophy, label: 'Quiz Arena' },
  { to: '/mock-exams', icon: FileText, label: 'Mock Exams' },
  { to: '/study-plan', icon: CalendarDays, label: 'Study Plan' },
  { to: '/leaderboard', icon: Crown, label: 'Rankings' },
  { to: '/profile', icon: User, label: 'Profile' },
];

export default function Sidebar({ onClose, onFeedbackOpen }) {
  const { user, logout } = useAuth();
  const navigationId = useId();
  const { plan, isPaid } = useSubscription();
  const nav = useNavigate();

  const handleLogout = async () => {
    await logout();
    nav('/login', { replace: true });
  };

  const xp = user?.xp || 0;
  const level = Math.max(1, Math.floor(xp / 500) + 1);
  const xpInLevel = xp % 500;

  return (
    <div className="ace-sidebar h-full flex flex-col bg-zinc-950 border-r border-white/5 py-4">
      {/* Logo */}
      <div className="px-5 mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2.5">
          <img src="/aceit-logo.png" alt="Ace It" className="h-16 w-auto object-contain" />
        </div>
        {onClose && (
          <button onClick={onClose} className="p-1.5 rounded-lg hover:bg-white/5 text-zinc-500 hover:text-white transition-colors">
            <X size={16} />
          </button>
        )}
      </div>

      {/* User Card */}
      {user && (
        <div className="mx-3 mb-4 p-3 rounded-xl glass-surface border border-white/5">
          <div className="flex items-center gap-3 mb-2">
            <div className="ace-avatar w-9 h-9 rounded-full flex items-center justify-center text-black font-heading font-black text-sm flex-shrink-0">
              {user.name?.[0]?.toUpperCase() || 'U'}
            </div>
            <div className="min-w-0 flex-1">
              <p className="text-white text-sm font-body font-semibold truncate">{user.name}</p>
              <p className="text-zinc-500 text-xs font-body">{user?.school === 'nios' ? 'Secondary Course Student' : `Class ${user?.class_level || '9'} Student`}</p>
            </div>
            <div className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-400/15 border border-amber-400/30">
              <Zap size={11} className="text-amber-400" />
              <span className="text-amber-400 text-xs font-heading font-bold">Lv{level}</span>
            </div>
          </div>

          {/* XP Bar */}
          <div>
            <div className="flex items-center justify-between mb-1">
              <span className="text-zinc-600 text-xs font-body">{xp} XP</span>
              <span className="text-zinc-600 text-xs font-body">{500 - xpInLevel} to next</span>
            </div>
            <div className="ace-xp-track h-1.5 bg-zinc-800 rounded-full overflow-hidden">
              <motion.div
                className="ace-xp-fill h-full rounded-full"
                style={{ background: 'var(--ace-cloud)' }}
                initial={{ width: 0 }}
                animate={{ width: `${(xpInLevel / 500) * 100}%` }}
                transition={{ duration: 1, delay: 0.3 }}
              />
            </div>
          </div>

          {/* Streak */}
          {user.streak > 0 && (
            <div className="flex items-center gap-1.5 mt-2">
              <Flame size={13} className="text-orange-400" />
              <span className="text-orange-400 text-xs font-body font-medium">{user.streak} day streak</span>
            </div>
          )}
        </div>
      )}

      {/* Navigation */}
      <LayoutGroup id={navigationId}>
      <nav className="flex-1 px-3 space-y-0.5 overflow-y-auto min-h-0">
        {NAV.map(({ to, icon: Icon, label, exact }) => (
          <NavLink
            key={to}
            to={to}
            end={exact}
            onClick={() => onClose?.()}
            data-testid={`nav-${label.toLowerCase().replace(' ', '-')}`}
            className={({ isActive }) =>
              `relative flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                isActive
                  ? 'bg-cyan-500/10 border border-cyan-500/20 text-cyan-400'
                  : 'text-zinc-500 hover:text-white hover:bg-white/5'
              }`
            }
          >
            {({ isActive }) => (
              <>
                {isActive && <motion.span aria-hidden="true" className="ace-nav-indicator" layoutId="active-navigation" transition={{ type: 'spring', stiffness: 420, damping: 34 }} />}
                <Icon size={18} className={isActive ? 'text-cyan-400' : 'text-zinc-500 group-hover:text-white transition-colors'} />
                <span className="text-sm font-body font-medium flex-1">{label}</span>
                {isActive && <ChevronRight size={14} className="text-cyan-500/50" />}
              </>
            )}
          </NavLink>
        ))}

        {/* Admin link — only for admin emails */}
        {user && ADMIN_EMAILS.includes(user.email?.toLowerCase()) && (
          <NavLink to="/admin" data-testid="nav-admin" onClick={() => onClose?.()}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all duration-200 group ${
                isActive ? 'bg-red-500/10 border border-red-500/20 text-red-400' : 'text-zinc-500 hover:text-red-400 hover:bg-red-500/5'
              }`
            }>
            {({ isActive }) => (
              <>
                <ShieldCheck size={18} className={isActive ? 'text-red-400' : 'text-zinc-500 group-hover:text-red-400 transition-colors'} />
                <span className="text-sm font-body font-medium flex-1">Admin</span>
              </>
            )}
          </NavLink>
        )}
      </nav>
      </LayoutGroup>

      {/* Bottom */}
      <div className="px-3 mt-2 space-y-2">
        {/* Upgrade CTA — only for free users */}
        {plan && !isPaid && (
          <button data-ace-primary="true" onClick={() => nav('/upgrade')} data-testid="sidebar-upgrade-btn"
            className="w-full flex items-center gap-2 px-3 py-2.5 rounded-xl text-amber-300 bg-gradient-to-r from-amber-500/15 via-rose-500/15 to-violet-500/15 border border-amber-400/30 hover:border-amber-400/60 transition-all text-sm font-body font-bold relative overflow-hidden group">
            <motion.div className="absolute inset-0 bg-gradient-to-r from-transparent via-amber-400/10 to-transparent"
              animate={{ x: ['-100%', '100%'] }} transition={{ duration: 2.5, repeat: Infinity, ease: 'linear' }} />
            <Sparkles size={16} className="relative z-10 text-amber-400" />
            <span className="relative z-10 flex-1 text-left">Upgrade to Starter</span>
            <span className="relative z-10 text-amber-400 text-[10px] font-body uppercase tracking-wider">₹399</span>
          </button>
        )}
        {plan && isPaid && (
          <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-gradient-to-r from-amber-500/20 to-rose-500/20 border border-amber-400/40">
            <Crown size={14} className="text-amber-400" />
            <span className="text-amber-300 text-xs font-heading font-bold flex-1">{plan.plan_name} Active</span>
          </div>
        )}

        <button onClick={handleLogout} data-testid="logout-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-500 hover:text-red-400 hover:bg-red-500/5 transition-all text-sm font-body">
          <LogOut size={18} />
          <span>Sign Out</span>
        </button>

        {/* Feedback button */}
        <button onClick={() => { onClose?.(); onFeedbackOpen?.(); }} data-testid="sidebar-feedback-btn"
          className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl text-zinc-500 hover:text-cyan-400 hover:bg-cyan-500/5 transition-all text-sm font-body">
          <MessageSquarePlus size={18} />
          <span>Send Feedback</span>
        </button>
      </div>
    </div>
  );
}
